"use client";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download } from "lucide-react";
import { formatCurrency, formatDate } from "@/lib/utils";
import { format } from "date-fns";
import { PaymentOutput } from "@/types/payment.types";
import { Customer, Vehicle } from "@/types";
import { generatePaymentReceiptPdf } from "@/lib/invoicePdf";

interface ReceiptViewProps {
  payment: PaymentOutput;
  customer?: Customer | null;
  vehicles?: Vehicle[];
}

export function ReceiptView({ payment, customer, vehicles = [] }: ReceiptViewProps) {
  const getPaymentStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      PENDING: "bg-yellow-500 hover:bg-yellow-600",
      COMPLETED: "bg-green-500 hover:bg-green-600",
      FAILED: "bg-red-500 hover:bg-red-600",
    };

    return (
      <Badge className={colors[status] || "bg-gray-500 hover:bg-gray-600"}>
        {status}
      </Badge>
    );
  };

  const handleDownloadReceipt = () => {
    try {
      generatePaymentReceiptPdf({
        payment,
        customer: customer || null,
        vehicles: vehicles,
      });
    } catch (error) {
      console.error("Error downloading payment receipt:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Company Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            WeClean Car Wash
          </h1>
          <p className="text-gray-600">We come to you</p>
        </div>

        {/* Receipt Card */}
        <Card className="shadow-lg">
          <CardContent className="p-8">
            {/* Title */}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Payment Receipt
              </h2>
              <p className="text-sm text-gray-500">
                Payment ID: <span className="font-mono">{payment.id}</span>
              </p>
            </div>

            {/* Amount - Prominent Display */}
            <div className="text-center mb-8 pb-8 border-b">
              <p className="text-sm text-gray-600 mb-2">Amount Paid</p>
              <p className="text-5xl font-bold text-gray-900">
                {formatCurrency(payment.paidAmount)}
              </p>
            </div>

            {/* Payment Details */}
            <div className="space-y-6 mb-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Payment Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Payment ID</p>
                    <p className="font-medium font-mono text-sm">{payment.id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Date</p>
                    <p className="font-medium">
                      {payment.refType === "APPOINTMENT" && payment.appointmentScheduledAt
                        ? format(new Date(payment.appointmentScheduledAt), "MMM dd, yyyy HH:mm")
                        : payment.date
                          ? formatDate(new Date(payment.date))
                          : "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total Amount</p>
                    <p className="text-lg font-bold">
                      {formatCurrency(payment.totalAmount)}
                    </p>
                  </div>
                  {payment.discount > 0 && (
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Discount</p>
                      <p className="text-lg font-semibold text-blue-600">
                        -{formatCurrency(payment.discount)}
                      </p>
                    </div>
                  )}
                  {payment.platinumDiscount > 0 && (
                    <div>
                      <p className="text-sm text-gray-600 mb-1">
                        Platinum Discount (15%)
                      </p>
                      <p className="text-lg font-semibold text-amber-600">
                        -{formatCurrency(payment.platinumDiscount)}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Paid Amount</p>
                    <p className="text-lg font-semibold text-green-600">
                      {formatCurrency(payment.paidAmount)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Due Amount</p>
                    <p className="text-lg font-semibold text-orange-600">
                      {formatCurrency(payment.dueAmount)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Payment Status</p>
                    <div>{getPaymentStatusBadge(payment.status)}</div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Payment Mode</p>
                    <p className="font-medium capitalize">{payment.mode}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Reference Type</p>
                    <p className="font-medium">
                      <Badge variant="outline">{payment.refType}</Badge>
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Reference ID</p>
                    <p className="font-medium font-mono text-sm">
                      {payment.refId}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Service Type</p>
                    <p className="font-medium">
                      {payment.serviceType ? (
                        <Badge variant="outline" className="capitalize">
                          {payment.serviceType === "one-time" ? "One-Time" : "Subscription"}
                        </Badge>
                      ) : (
                        "N/A"
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Customer Information */}
              {(customer || payment.customer) && (
                <div className="pt-6 border-t">
                  <p className="text-sm text-gray-600 mb-2">Customer:</p>
                  <div>
                    <p className="font-medium text-gray-900">
                      {customer?.name || payment.customer?.name || "N/A"}
                    </p>
                    {customer && (
                      <>
                        {customer.phone && (
                          <p className="text-sm text-gray-600 mt-1">
                            {customer.phone}
                          </p>
                        )}
                        {customer.email && (
                          <p className="text-sm text-gray-600 mt-1">
                            {customer.email}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Vehicle Information */}
              {(vehicles.length > 0 || payment.vehicle) && (
                <div className="pt-6 border-t">
                  <p className="text-sm text-gray-600 mb-2">
                    Vehicle{vehicles.length > 1 ? "s" : ""}:
                  </p>
                  <div className="space-y-2">
                    {vehicles.length > 0 ? (
                      vehicles.map((vehicle, index) => (
                        <p key={vehicle.id || index} className="font-medium">
                          {`${vehicle.make} ${vehicle.model}${
                            vehicle.plateNumber ? ` - ${vehicle.plateNumber}` : ""
                          }`}
                        </p>
                      ))
                    ) : payment.vehicle ? (
                      <p className="font-medium">
                        {`${payment.vehicle.make} ${payment.vehicle.model}`}
                      </p>
                    ) : (
                      <p className="font-medium">N/A</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Download Button */}
            <div className="flex justify-center pt-6 border-t">
              <Button
                onClick={handleDownloadReceipt}
                size="lg"
                className="min-w-[200px]"
              >
                <Download className="mr-2 h-4 w-4" />
                Download Receipt
              </Button>
            </div>

            {/* Footer */}
            <div className="mt-8 pt-6 border-t text-center">
              <p className="text-sm text-gray-500">
                Thank you for your payment!
              </p>
              <p className="text-xs text-gray-400 mt-2">
                WeClean Car Wash • +974 7090 8373 • info@weclean.com.qa
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

